import { ApiCompany } from "./company";
import { ApiBarragem } from "./barragem";
import { ApiZAS } from "./zas";
import { ApiSection } from "./section";
import { ApiDamFiles } from "./barragemFiles";
import { ApiUser } from "./user";
import { ApiContrato } from "./contratos";
import { ApiEstadosMunicipios } from "./estadoMunicipio";
import { ApiSeminarios } from "./seminarios";
import { ApiSegurancaBarragems } from "./segBarragem";
import { ApiTreinamento } from "./treinamentos";
import { ApiSimulado } from "./simulados";
import { ApiOficio } from "./oficios";
import { ApiControle } from "./controle";
import { ApiAcomodacao } from "./acomodacoes";
import { ApiFluxogramas } from "./fluxograma";
import { ApiContato } from "./contatos";
import axios, { AxiosResponse, isAxiosError } from "axios";
import { ApiManchas } from "./manchas";
import { ApiGISTM } from "./gistm";

export interface RequestError {
  error: string;
  message: string;
}

export interface Paginated<T> {
  items: T[];
  page: number;
  perPage: number;
  totalPages: number;
  totalItems: number;
}

export interface MinIOFilePart {
  url: string;
  partNumber: number;
}

export interface MinIOMultipartUpload {
  uploadId: string;
  parts: MinIOFilePart[];
  nPartes: number;
  partSize: number;
}

export interface MinIOMultipartUploadWithId {
  id: number | string;
  upload: MinIOMultipartUpload;
}

// Schemas
export interface ApiSchema {
  company: typeof ApiCompany;
  barragem: typeof ApiBarragem;
  zas: typeof ApiZAS;
  section: typeof ApiSection;
  damFiles: typeof ApiDamFiles;
  user: typeof ApiUser;
  contato: typeof ApiContato;
  contrato: typeof ApiContrato;
  estadosMunicipios: typeof ApiEstadosMunicipios;
  seminarios: typeof ApiSeminarios;
  segBarragem: typeof ApiSegurancaBarragems;
  treinamentos: typeof ApiTreinamento;
  simulados: typeof ApiSimulado;
  oficios: typeof ApiOficio;
  controle: typeof ApiControle;
  acomodacoes: typeof ApiAcomodacao;
  fluxogramas: typeof ApiFluxogramas;
  manchas: typeof ApiManchas;
  gistm: typeof ApiGISTM;
  baseUrl: string;
}

// Dados da API
export function useApi(): ApiSchema {
  return {
    company: ApiCompany,
    barragem: ApiBarragem,
    zas: ApiZAS,
    section: ApiSection,
    damFiles: ApiDamFiles,
    user: ApiUser,
    contato: ApiContato,
    contrato: ApiContrato,
    estadosMunicipios: ApiEstadosMunicipios,
    segBarragem: ApiSegurancaBarragems,
    treinamentos: ApiTreinamento,
    simulados: ApiSimulado,
    oficios: ApiOficio,
    seminarios: ApiSeminarios,
    controle: ApiControle,
    acomodacoes: ApiAcomodacao,
    fluxogramas: ApiFluxogramas,
    manchas: ApiManchas,
    gistm: ApiGISTM,
    baseUrl: `${process.env.REACT_APP_URL}`,
  };
}

// Adicionar arquivo no MinIO

/**
 * 
 * @param urlParts 
 * @param urlComplete 
 * @param file 
 * @param onProgess 
 * @returns 
 * 
 * Caso de Uso com ID de Upload
 * Caso o servidor retorne um ID de upload, utilize a URL de complete sem o ID no final da URL
 * 
 * @exemple
 * 
  const uploadFile = async (idSimulado: number, file: File) => {

    const result = await setFile<GISTMCheckFiles>(
      `${url}/file_upload_parts/${idSimulado}`, 
      `${url}/file_upload_complete/${idSimulado}`,
      file,
      (percent) => {
        console.log(`Upload progress: ${percent.toFixed(2)}%`);
      }
    );
    if ("error" in result) {
      console.error("Erro ao fazer upload do arquivo:", result.message);
    } else {
      console.log("Arquivo enviado com sucesso:", result);
    }
  };
 */
export const setFile = async <T>(
  urlParts: string,
  urlComplete: string,
  file: File,
  onProgess?: (percent: number) => void
): Promise<T | RequestError> => {
  let uploadData:
    | undefined
    | MinIOMultipartUpload
    | MinIOMultipartUploadWithId = undefined;

  // Upload Parts
  try {
    const data: AxiosResponse<
      MinIOMultipartUpload | MinIOMultipartUploadWithId
    > = await axios.put(urlParts, {
      filename: file.name,
      filesize: file.size,
    });
    uploadData = data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Upload Parts Error",
        message:
          error.response?.data.message ??
          "Erro ao obter partes para o upload dos arquivos",
      };
    } else {
      throw error;
    }
  }

  if (uploadData === undefined) {
    return {
      error: "Upload Parts Error",
      message: "Erro ao obter partes para o upload dos arquivos",
    };
  } else if ("id" in uploadData) {
    urlComplete = `${urlComplete}/${uploadData.id}`;
    uploadData = uploadData.upload;
  }

  const totalChunks = uploadData.nPartes;
  const CHUNK_SIZE = uploadData.partSize;

  onProgess?.(0);

  // Uploads
  const uploads = uploadData.parts.map(async (part) => {
    const start = (part.partNumber - 1) * CHUNK_SIZE;
    const end = Math.min(file.size, start + CHUNK_SIZE);
    const chunk = file.slice(start, end);

    try {
      const response = await fetch(part.url, {
        method: "PUT",
        body: chunk,
      });

      if (!response.ok) {
        throw new Error(`Falha no upload do chunk`);
      }

      const percent = (part.partNumber * 100) / totalChunks;
      onProgess?.(percent);

      const ETag = response.headers.get("ETag");
      if (!ETag) {
        throw new Error(`ETag não encontrado na parte ${part.partNumber}`);
      }

      return {
        ETag: ETag.replace(/"/g, ""),
        PartNumber: part.partNumber,
      };
    } catch (error) {
      throw new Error(`Falha no upload do chunk`);
    }
  });

  const partes = await Promise.all(uploads);

  // Upload Complete
  try {
    const data: AxiosResponse<T> = await axios.put(urlComplete, {
      partes: partes,
    });
    return data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Complete Error",
        message:
          error.response?.data.message ??
          "Erro ao completar a adição do arquivo",
      };
    } else {
      throw error;
    }
  }
};
